import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import {
    Trophy, Users, Calendar, GripVertical, ArrowUp, ArrowDown,
    Shuffle, Play, Settings, Table, GitBranch, Repeat, ChevronRight,
    Plus, Trash2, CheckCircle, RefreshCw, Eye, AlertTriangle, Edit,
    Timer, Medal, Waves, Activity, User, Clock, MapPin, Coffee
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default match durations by sport (in minutes)
const SPORT_DURATIONS = {
    'Football': { duration: 90, extraTime: 30 },
    'Basketball': { duration: 48, extraTime: 10 },
    'Hockey': { duration: 70, extraTime: 20 },
    'Rugby': { duration: 80, extraTime: 20 },
    'Volleyball': { duration: 90, extraTime: 0 },
    'Netball': { duration: 60, extraTime: 14 },
    'Handball': { duration: 60, extraTime: 10 },
    'Tennis': { duration: 120, extraTime: 0 },
    'Badminton': { duration: 60, extraTime: 0 },
    'Table Tennis': { duration: 45, extraTime: 0 },
    'Swimming': { duration: 5, extraTime: 0 },
    'Athletics Track and Field': { duration: 10, extraTime: 0 },
    'default': { duration: 60, extraTime: 15 }
};

const BracketTab = ({ tournaments, token }) => {
    const [selectedTournament, setSelectedTournament] = useState(null);
    const [registeredTeams, setRegisteredTeams] = useState([]);
    const [seededTeams, setSeededTeams] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [bracket, setBracket] = useState(null);
    
    // Sport type tab
    const [sportTypeTab, setSportTypeTab] = useState('team');
    
    // Configuration for team sports
    const [format, setFormat] = useState('knockout');
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [matchesPerDay, setMatchesPerDay] = useState(4);
    const [venue, setVenue] = useState('Main Venue');
    const [homeAndAway, setHomeAndAway] = useState(false);
    
    // Time Scheduling Configuration
    const [enableTimeScheduling, setEnableTimeScheduling] = useState(true);
    const [startTime, setStartTime] = useState('09:00');
    const [matchDuration, setMatchDuration] = useState(90);
    const [intervalBetweenMatches, setIntervalBetweenMatches] = useState(15);
    const [extraTimeBuffer, setExtraTimeBuffer] = useState(30);
    const [numVenues, setNumVenues] = useState(1);
    const [venueNames, setVenueNames] = useState(['Main Venue']);
    const [enableBreaks, setEnableBreaks] = useState(false);
    const [breakStartTime, setBreakStartTime] = useState('12:00');
    const [breakDuration, setBreakDuration] = useState(60);
    
    // Configuration for individual sports
    const [individualFormat, setIndividualFormat] = useState('heats_finals');
    const [sportType, setSportType] = useState('swimming');
    const [eventName, setEventName] = useState('');
    const [athletesPerHeat, setAthletesPerHeat] = useState(8);
    const [qualifiersPerHeat, setQualifiersPerHeat] = useState(2);
    const [timeQualifiers, setTimeQualifiers] = useState(2);
    const [seededAthletes, setSeededAthletes] = useState([]);
    const [individualEvents, setIndividualEvents] = useState(null);
    
    // Individual Sports Time Scheduling
    const [individualStartTime, setIndividualStartTime] = useState('09:00');
    const [heatDuration, setHeatDuration] = useState(5);
    const [intervalBetweenHeats, setIntervalBetweenHeats] = useState(10);
    const [enableIndividualBreaks, setEnableIndividualBreaks] = useState(false);
    const [individualBreakStartTime, setIndividualBreakStartTime] = useState('12:00');
    const [individualBreakDuration, setIndividualBreakDuration] = useState(60);
    
    // Age Categories
    const [selectedAgeCategories, setSelectedAgeCategories] = useState(['Open']);
    const [ageCategorySchedules, setAgeCategorySchedules] = useState([
        { category: 'Open', startTime: '09:00', numVenues: 1, venueNames: ['Main Venue'] }
    ]);
    const [enableAgeCategoryScheduling, setEnableAgeCategoryScheduling] = useState(false);
    
    // Available age categories
    const AGE_CATEGORIES = [
        'Under 7', 'Under 9', 'Under 11', 'Under 13', 
        'Under 15', 'Under 17', 'Under 19', 'Under 21', 
        'Senior', 'Open', 'Masters'
    ];
    
    // Multi-Discipline Scheduling (for athletics, swimming with parallel events)
    const [scheduledDisciplines, setScheduledDisciplines] = useState([]);
    const [showAddDisciplineDialog, setShowAddDisciplineDialog] = useState(false);
    const [newDiscipline, setNewDiscipline] = useState({
        name: '',
        venue: 'Track',
        startTime: '09:00',
        duration: 10,
        interval: 5,
        athletesPerHeat: 8,
        qualifiersPerHeat: 2,
        timeQualifiers: 2,
        ageCategory: 'Open',
        numVenues: 1,
        venueNames: ['Track 1']
    });
    
    // Available venues/areas for multi-discipline events
    const [individualVenues, setIndividualVenues] = useState([
        { id: 1, name: 'Track', type: 'track' },
        { id: 2, name: 'Field Area 1', type: 'field' },
        { id: 3, name: 'Field Area 2', type: 'field' },
        { id: 4, name: 'Pool Lane 1-4', type: 'pool' },
        { id: 5, name: 'Pool Lane 5-8', type: 'pool' }
    ]);
    
    // Discipline categories with default durations
    const disciplineDefaults = {
        // Track events
        '100 Meters': { duration: 3, venue: 'Track', type: 'track' },
        '200 Meters': { duration: 4, venue: 'Track', type: 'track' },
        '400 Meters': { duration: 5, venue: 'Track', type: 'track' },
        '800 Meters': { duration: 8, venue: 'Track', type: 'track' },
        '1500 Meters': { duration: 12, venue: 'Track', type: 'track' },
        '3000 Meters': { duration: 20, venue: 'Track', type: 'track' },
        '400 Meters Relay': { duration: 5, venue: 'Track', type: 'track' },
        '800 Meters Relay': { duration: 8, venue: 'Track', type: 'track' },
        // Field events (can run parallel to track)
        'Long Jump': { duration: 45, venue: 'Field Area 1', type: 'field' },
        'High Jump': { duration: 60, venue: 'Field Area 1', type: 'field' },
        'Triple Jump': { duration: 45, venue: 'Field Area 1', type: 'field' },
        'Shot Put': { duration: 30, venue: 'Field Area 2', type: 'field' },
        'Discus Throw': { duration: 35, venue: 'Field Area 2', type: 'field' },
        'Javelin Throw': { duration: 35, venue: 'Field Area 2', type: 'field' },
        // Swimming events
        '50m Freestyle': { duration: 3, venue: 'Pool', type: 'pool' },
        '100m Freestyle': { duration: 5, venue: 'Pool', type: 'pool' },
        '200m Freestyle': { duration: 8, venue: 'Pool', type: 'pool' },
        '50m Backstroke': { duration: 4, venue: 'Pool', type: 'pool' },
        '100m Backstroke': { duration: 6, venue: 'Pool', type: 'pool' },
        '50m Breaststroke': { duration: 4, venue: 'Pool', type: 'pool' },
        '100m Breaststroke': { duration: 6, venue: 'Pool', type: 'pool' },
        '50m Butterfly': { duration: 4, venue: 'Pool', type: 'pool' },
        '100m Butterfly': { duration: 6, venue: 'Pool', type: 'pool' }
    };
    
    // Dialog states
    const [showPreviewDialog, setShowPreviewDialog] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [matchesWithScores, setMatchesWithScores] = useState(0);
    const [showIndividualPreviewDialog, setShowIndividualPreviewDialog] = useState(false);
    
    // Edit match dialog
    const [showEditMatchDialog, setShowEditMatchDialog] = useState(false);
    const [editingMatch, setEditingMatch] = useState(null);
    const [editMatchForm, setEditMatchForm] = useState({
        home_score: 0,
        away_score: 0,
        scheduled_date: '',
        scheduled_time: '',
        venue: ''
    });
    
    // Edit heat dialog
    const [showEditHeatDialog, setShowEditHeatDialog] = useState(false);
    const [editingHeat, setEditingHeat] = useState(null);
    const [heatResults, setHeatResults] = useState([]);

    const api = axios.create({
        baseURL: `${API_URL}/api`,
        headers: { Authorization: `Bearer ${token}` }
    });

    useEffect(() => {
        if (selectedTournament) {
            loadTeams();
            loadBracket();
            loadIndividualEvents();
            
            // Auto-fill match duration based on tournament sport
            const sport = selectedTournament.sport;
            const sportSettings = SPORT_DURATIONS[sport] || SPORT_DURATIONS['default'];
            setMatchDuration(sportSettings.duration);
            setExtraTimeBuffer(sportSettings.extraTime);
        }
    }, [selectedTournament]);

    const loadTeams = async () => {
        setLoading(true);
        try {
            // Get registered teams for this tournament
            const res = await api.get(`/tournaments/${selectedTournament.id}/registrations`);
            const teams = (res.data || [])
                .filter(r => r.registration_type === 'team' && r.status === 'confirmed')
                .map((r, idx) => ({
                    team_id: r.id,
                    team_name: r.team_name,
                    seed: idx + 1,
                    player_count: r.player_count || r.players?.length || 0
                }));
            setRegisteredTeams(teams);
            setSeededTeams(teams);
            
            // Also load individual athletes
            const athletes = (res.data || [])
                .filter(r => r.registration_type === 'individual' && r.status === 'confirmed')
                .map((r, idx) => ({
                    athlete_id: r.id,
                    athlete_name: r.athlete_name || r.name || `Athlete ${idx + 1}`,
                    seed: idx + 1,
                    personal_best: r.personal_best || null
                }));
            setSeededAthletes(athletes);
        } catch (error) {
            console.error('Failed to load teams:', error);
            setRegisteredTeams([]);
            setSeededTeams([]);
            setSeededAthletes([]);
        } finally {
            setLoading(false);
        }
    };

    const loadBracket = async () => {
        try {
            const res = await api.get(`/brackets/tournament/${selectedTournament.id}`);
            setBracket(res.data);
        } catch (error) {
            setBracket(null);
        }
    };
    
    const loadIndividualEvents = async () => {
        try {
            const res = await api.get(`/brackets/individual/tournament/${selectedTournament.id}`);
            setIndividualEvents(res.data);
        } catch (error) {
            setIndividualEvents(null);
        }
    };

    // Multi-discipline management functions
    const addDiscipline = () => {
        if (!newDiscipline.name) {
            toast.error('Please select a discipline');
            return;
        }
        
        // Check for duplicate - same discipline AND same age category
        const duplicateKey = `${newDiscipline.ageCategory || 'Open'}_${newDiscipline.name}`;
        if (scheduledDisciplines.find(d => `${d.ageCategory || 'Open'}_${d.name}` === duplicateKey)) {
            toast.error(`${newDiscipline.ageCategory || 'Open'} ${newDiscipline.name} is already added`);
            return;
        }
        
        const discipline = {
            ...newDiscipline,
            id: Date.now(),
            athletes: seededAthletes.map(a => ({ ...a })) // Copy athletes for this discipline
        };
        
        setScheduledDisciplines([...scheduledDisciplines, discipline]);
        setShowAddDisciplineDialog(false);
        
        // Auto-increment start time for next discipline
        const [h, m] = newDiscipline.startTime.split(':').map(Number);
        const totalMins = h * 60 + m + newDiscipline.duration + newDiscipline.interval + 15;
        const newH = Math.floor(totalMins / 60) % 24;
        const newM = totalMins % 60;
        const nextTime = `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
        
        setNewDiscipline({
            name: '',
            venue: 'Track',
            startTime: nextTime,
            duration: 10,
            interval: 5,
            athletesPerHeat: 8,
            qualifiersPerHeat: 2,
            timeQualifiers: 2,
            ageCategory: 'Open',
            numVenues: 1,
            venueNames: ['Track 1']
        });
        toast.success(`Added ${discipline.ageCategory !== 'Open' ? discipline.ageCategory + ' ' : ''}${discipline.name} (${discipline.numVenues} venues) to schedule`);
    };
    
    const removeDiscipline = (id) => {
        setScheduledDisciplines(scheduledDisciplines.filter(d => d.id !== id));
        toast.info('Discipline removed');
    };
    
    const updateDiscipline = (id, field, value) => {
        setScheduledDisciplines(scheduledDisciplines.map(d => 
            d.id === id ? { ...d, [field]: value } : d
        ));
    };
    
    const handleDisciplineSelect = (disciplineName) => {
        const defaults = disciplineDefaults[disciplineName] || { duration: 10, venue: 'Track' };
        setNewDiscipline({
            ...newDiscipline,
            name: disciplineName,
            duration: defaults.duration,
            venue: defaults.venue
        });
    };
    
    const calculateScheduleEndTime = (startTime, duration, interval, numHeats) => {
        const [h, m] = startTime.split(':').map(Number);
        const totalMins = h * 60 + m + (numHeats * (duration + interval));
        const endH = Math.floor(totalMins / 60) % 24;
        const endM = totalMins % 60;
        return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
    };
    
    const generateMultiDisciplineSchedule = async () => {
        if (scheduledDisciplines.length === 0) {
            toast.error('Please add at least one discipline');
            return;
        }
        
        setGenerating(true);
        try {
            const payload = {
                tournament_id: selectedTournament.id,
                disciplines: scheduledDisciplines.map(d => ({
                    event_name: d.ageCategory && d.ageCategory !== 'Open' ? `${d.ageCategory} ${d.name}` : d.name,
                    venue: d.venue,
                    start_time: d.startTime,
                    duration: d.duration,
                    interval: d.interval,
                    athletes_per_heat: d.athletesPerHeat,
                    qualifiers_per_heat: d.qualifiersPerHeat,
                    time_qualifiers: d.timeQualifiers,
                    age_category: d.ageCategory || 'Open',
                    num_venues: d.numVenues || 1,
                    venue_names: (d.venueNames || []).slice(0, d.numVenues || 1),
                    athletes: d.athletes.map(a => ({
                        athlete_id: a.athlete_id,
                        athlete_name: a.athlete_name,
                        seed: a.seed,
                        personal_best: a.personal_best
                    }))
                })),
                sport_type: sportType,
                format: individualFormat,
                start_date: startDate,
                enable_breaks: enableIndividualBreaks,
                break_start_time: individualBreakStartTime,
                break_duration: individualBreakDuration
            };
            
            const res = await api.post('/brackets/individual/multi-discipline', payload);
            toast.success(`Generated schedule for ${scheduledDisciplines.length} disciplines!`);
            setIndividualEvents(res.data);
            setShowIndividualPreviewDialog(true);
            loadIndividualEvents();
        } catch (error) {
            console.error('Failed to generate multi-discipline schedule:', error);
            toast.error(error.response?.data?.detail || 'Failed to generate schedule');
        } finally {
            setGenerating(false);
        }
    };

    const moveSeed = (index, direction) => {
        const newSeeds = [...seededTeams];
        const targetIndex = index + direction;
        
        if (targetIndex < 0 || targetIndex >= newSeeds.length) return;
        
        // Swap teams
        const temp = newSeeds[index];
        newSeeds[index] = newSeeds[targetIndex];
        newSeeds[targetIndex] = temp;
        
        // Update seed numbers
        newSeeds.forEach((team, i) => {
            team.seed = i + 1;
        });
        
        setSeededTeams(newSeeds);
    };

    const randomizeSeeds = () => {
        const shuffled = [...seededTeams].sort(() => Math.random() - 0.5);
        shuffled.forEach((team, i) => {
            team.seed = i + 1;
        });
        setSeededTeams(shuffled);
        toast.success('Seeds randomized!');
    };

    const checkExistingScores = async () => {
        try {
            const res = await api.get(`/brackets/tournament/${selectedTournament.id}/has-scores`);
            return res.data;
        } catch (error) {
            return { has_scores: false, matches_with_scores: 0 };
        }
    };

    const generateBracket = async (force = false) => {
        if (seededTeams.length < 2) {
            toast.error('Need at least 2 teams to generate bracket');
            return;
        }
        
        // Check for existing scores first (unless force is true)
        if (!force) {
            const scoreCheck = await checkExistingScores();
            if (scoreCheck.has_scores) {
                setMatchesWithScores(scoreCheck.matches_with_scores);
                setShowConfirmDialog(true);
                return;
            }
        }
        
        setGenerating(true);
        try {
            const payload = {
                tournament_id: selectedTournament.id,
                format: format,
                teams: seededTeams.map(t => ({
                    team_id: t.team_id,
                    team_name: t.team_name,
                    seed: t.seed
                })),
                start_date: startDate,
                matches_per_day: matchesPerDay,
                venue: venue,
                home_and_away: homeAndAway,
                force: force
            };
            
            // Add time scheduling configuration if enabled
            if (enableTimeScheduling) {
                payload.time_schedule = {
                    start_time: startTime,
                    match_duration: matchDuration,
                    interval_between_matches: intervalBetweenMatches,
                    extra_time_buffer: format === 'knockout' ? extraTimeBuffer : 0,
                    num_venues: numVenues,
                    venue_names: venueNames.slice(0, numVenues),
                    enable_breaks: enableBreaks,
                    break_start_time: breakStartTime,
                    break_duration: breakDuration
                };
            }
            
            const res = await api.post('/brackets/generate', payload);
            toast.success(`Generated ${res.data.total_matches} matches!`);
            setBracket(res.data);
            setShowPreviewDialog(true);
            setShowConfirmDialog(false);
        } catch (error) {
            // Handle the 409 conflict error specifically
            if (error.response?.status === 409) {
                const detail = error.response?.data?.detail || '';
                const match = detail.match(/(\d+) matches with scores/);
                if (match) {
                    setMatchesWithScores(parseInt(match[1]));
                }
                setShowConfirmDialog(true);
            } else {
                toast.error(error.response?.data?.detail || 'Failed to generate bracket');
            }
        } finally {
            setGenerating(false);
        }
    };

    const handleEditMatch = (match) => {
        setEditingMatch(match);
        setEditMatchForm({
            home_score: match.home_score || match.score1 || 0,
            away_score: match.away_score || match.score2 || 0,
            scheduled_date: match.scheduled_date || match.date || '',
            scheduled_time: match.scheduled_time || match.time || '',
            venue: match.venue || 'Main Venue'
        });
        setShowEditMatchDialog(true);
    };

    const saveMatchEdit = async () => {
        if (!editingMatch) return;
        
        try {
            await api.put(`/matches/${editingMatch.id}`, {
                home_score: editMatchForm.home_score,
                away_score: editMatchForm.away_score,
                score1: editMatchForm.home_score,
                score2: editMatchForm.away_score,
                scheduled_date: editMatchForm.scheduled_date,
                scheduled_time: editMatchForm.scheduled_time,
                venue: editMatchForm.venue
            });
            toast.success('Match updated successfully!');
            setShowEditMatchDialog(false);
            setEditingMatch(null);
            loadBracket(); // Refresh bracket data
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update match');
        }
    };
    
    // ==================== INDIVIDUAL SPORTS FUNCTIONS ====================
    
    const addManualAthlete = () => {
        const newAthlete = {
            athlete_id: `manual-${Date.now()}`,
            athlete_name: `Athlete ${seededAthletes.length + 1}`,
            seed: seededAthletes.length + 1,
            personal_best: null
        };
        setSeededAthletes([...seededAthletes, newAthlete]);
    };
    
    const updateAthleteName = (index, name) => {
        const updated = [...seededAthletes];
        updated[index].athlete_name = name;
        setSeededAthletes(updated);
    };
    
    const updateAthletePB = (index, pb) => {
        const updated = [...seededAthletes];
        updated[index].personal_best = pb;
        setSeededAthletes(updated);
    };
    
    const removeAthlete = (index) => {
        const updated = seededAthletes.filter((_, i) => i !== index);
        updated.forEach((a, i) => a.seed = i + 1);
        setSeededAthletes(updated);
    };
    
    const moveAthleteSeed = (index, direction) => {
        const newSeeds = [...seededAthletes];
        const targetIndex = index + direction;
        
        if (targetIndex < 0 || targetIndex >= newSeeds.length) return;
        
        [newSeeds[index], newSeeds[targetIndex]] = [newSeeds[targetIndex], newSeeds[index]];
        newSeeds[index].seed = index + 1;
        newSeeds[targetIndex].seed = targetIndex + 1;
        setSeededAthletes(newSeeds);
    };
    
    const randomizeAthleteSeeds = () => {
        const shuffled = [...seededAthletes].sort(() => Math.random() - 0.5);
        shuffled.forEach((a, i) => a.seed = i + 1);
        setSeededAthletes(shuffled);
        toast.success('Athlete seeds randomized!');
    };
    
    const generateIndividualHeats = async (force = false) => {
        if (seededAthletes.length < 2) {
            toast.error('Need at least 2 athletes to generate heats');
            return;
        }
        
        if (!eventName.trim()) {
            toast.error('Please enter an event name (e.g., "Men\'s 100m Freestyle")');
            return;
        }
        
        setGenerating(true);
        try {
            const payload = {
                tournament_id: selectedTournament.id,
                event_name: eventName,
                sport_type: sportType,
                format: individualFormat,
                athletes: seededAthletes.map(a => ({
                    athlete_id: a.athlete_id,
                    athlete_name: a.athlete_name,
                    seed: a.seed,
                    personal_best: a.personal_best
                })),
                start_date: startDate,
                heats_per_session: 4,
                athletes_per_heat: athletesPerHeat,
                qualifiers_per_heat: qualifiersPerHeat,
                time_qualifiers: timeQualifiers,
                venue: venue,
                force: force
            };
            
            // Add time scheduling for individual sports
            if (enableTimeScheduling) {
                payload.time_schedule = {
                    start_time: individualStartTime,
                    heat_duration: heatDuration,
                    interval_between_heats: intervalBetweenHeats,
                    num_lanes: athletesPerHeat,
                    num_venues: numVenues,
                    venue_names: venueNames.slice(0, numVenues),
                    enable_breaks: enableIndividualBreaks,
                    break_start_time: individualBreakStartTime,
                    break_duration: individualBreakDuration
                };
            }
            
            const res = await api.post('/brackets/individual/generate', payload);
            toast.success(`Generated ${res.data.total_events} heats for ${eventName}!`);
            setIndividualEvents(res.data);
            setShowIndividualPreviewDialog(true);
            loadIndividualEvents();
        } catch (error) {
            if (error.response?.status === 409) {
                toast.error('Event has existing results. Use force to regenerate.');
            } else {
                toast.error(error.response?.data?.detail || 'Failed to generate heats');
            }
        } finally {
            setGenerating(false);
        }
    };
    
    const handleEditHeat = (event) => {
        setEditingHeat(event);
        setHeatResults(event.participants.map(p => ({
            athlete_id: p.athlete_id,
            athlete_name: p.athlete_name,
            lane: p.lane,
            time: p.time || '',
            position: p.position || ''
        })));
        setShowEditHeatDialog(true);
    };
    
    const saveHeatResults = async () => {
        if (!editingHeat) return;
        
        try {
            // Sort by time and assign positions
            const sorted = [...heatResults]
                .filter(r => r.time)
                .sort((a, b) => parseFloat(a.time) - parseFloat(b.time));
            
            sorted.forEach((r, i) => r.position = i + 1);
            
            // Add non-finished athletes
            heatResults.forEach(r => {
                if (!r.time) {
                    r.position = null;
                }
            });
            
            await api.put(`/brackets/individual/event/${editingHeat.id}/results`, heatResults);
            toast.success('Heat results saved!');
            setShowEditHeatDialog(false);
            setEditingHeat(null);
            loadIndividualEvents();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save results');
        }
    };

    const getFormatIcon = (fmt) => {
        switch (fmt) {
            case 'knockout': return GitBranch;
            case 'round_robin': return Repeat;
            case 'league': return Table;
            default: return Trophy;
        }
    };

    const FormatIcon = getFormatIcon(format);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                    <h2 className="font-heading font-bold text-xl uppercase flex items-center gap-2">
                        <GitBranch className="w-6 h-6 text-primary" />
                        Bracket & Roster Generator
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        Create tournament brackets with team seedings
                    </p>
                </div>
                
                <Select 
                    value={selectedTournament?.id || ''} 
                    onValueChange={(v) => setSelectedTournament(tournaments?.find(t => t.id === v))}
                >
                    <SelectTrigger className="w-[250px] bg-[#0a1628] border-white/10" data-testid="tournament-select">
                        <SelectValue placeholder="Select Tournament" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d2847] border-white/10">
                        {tournaments?.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {!selectedTournament ? (
                <div className="bg-[#0d2847] border border-white/5 p-12 text-center">
                    <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Select a tournament to manage its bracket</p>
                </div>
            ) : (
                <Tabs value={sportTypeTab} onValueChange={setSportTypeTab} className="w-full">
                    <TabsList className="bg-[#0a1628] border border-white/10 p-1 mb-6">
                        <TabsTrigger 
                            value="team" 
                            className="data-[state=active]:bg-primary data-[state=active]:text-[#0a1628]"
                        >
                            <Users className="w-4 h-4 mr-2" />
                            Team Sports
                        </TabsTrigger>
                        <TabsTrigger 
                            value="individual" 
                            className="data-[state=active]:bg-primary data-[state=active]:text-[#0a1628]"
                        >
                            <Timer className="w-4 h-4 mr-2" />
                            Individual Sports
                        </TabsTrigger>
                    </TabsList>
                    
                    {/* Team Sports Tab */}
                    <TabsContent value="team">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Left: Seeding Panel */}
                            <div className="bg-[#0d2847] border border-white/5 p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-heading font-bold text-lg uppercase flex items-center gap-2">
                                        <Users className="w-5 h-5 text-primary" />
                                        Team Seedings
                                    </h3>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={randomizeSeeds}
                                        className="border-white/10"
                                    >
                                        <Shuffle className="w-4 h-4 mr-1" />
                                        Randomize
                                    </Button>
                                </div>
                        
                        {loading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : seededTeams.length > 0 ? (
                            <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                {seededTeams.map((team, index) => (
                                    <div 
                                        key={team.team_id}
                                        className="flex items-center gap-3 p-3 bg-[#0a1628] border border-white/10 hover:border-primary/30"
                                        data-testid={`seed-${team.seed}`}
                                    >
                                        <div className="w-8 h-8 bg-primary/20 flex items-center justify-center font-bold text-primary">
                                            {team.seed}
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-medium">{team.team_name}</p>
                                            <p className="text-xs text-muted-foreground">{team.player_count} players</p>
                                        </div>
                                        <div className="flex gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => moveSeed(index, -1)}
                                                disabled={index === 0}
                                                className="h-8 w-8 p-0"
                                            >
                                                <ArrowUp className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => moveSeed(index, 1)}
                                                disabled={index === seededTeams.length - 1}
                                                className="h-8 w-8 p-0"
                                            >
                                                <ArrowDown className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                <p>No registered teams found</p>
                                <p className="text-xs">Teams must be registered and confirmed to appear here</p>
                            </div>
                        )}
                    </div>

                    {/* Right: Configuration Panel */}
                    <div className="space-y-6">
                        {/* Format Selection */}
                        <div className="bg-[#0d2847] border border-white/5 p-6">
                            <h3 className="font-heading font-bold text-lg uppercase flex items-center gap-2 mb-4">
                                <Settings className="w-5 h-5 text-primary" />
                                Tournament Format
                            </h3>
                            
                            <div className="grid grid-cols-3 gap-3 mb-6">
                                <button
                                    onClick={() => setFormat('knockout')}
                                    className={`p-4 border text-center transition-all ${
                                        format === 'knockout'
                                            ? 'bg-primary/20 border-primary text-white'
                                            : 'bg-[#0a1628] border-white/10 text-muted-foreground hover:border-white/30'
                                    }`}
                                    data-testid="format-knockout"
                                >
                                    <GitBranch className="w-6 h-6 mx-auto mb-2" />
                                    <p className="font-semibold text-sm">Knockout</p>
                                    <p className="text-xs mt-1">Single elimination</p>
                                </button>
                                
                                <button
                                    onClick={() => setFormat('round_robin')}
                                    className={`p-4 border text-center transition-all ${
                                        format === 'round_robin'
                                            ? 'bg-primary/20 border-primary text-white'
                                            : 'bg-[#0a1628] border-white/10 text-muted-foreground hover:border-white/30'
                                    }`}
                                    data-testid="format-round-robin"
                                >
                                    <Repeat className="w-6 h-6 mx-auto mb-2" />
                                    <p className="font-semibold text-sm">Round Robin</p>
                                    <p className="text-xs mt-1">Everyone plays everyone</p>
                                </button>
                                
                                <button
                                    onClick={() => setFormat('league')}
                                    className={`p-4 border text-center transition-all ${
                                        format === 'league'
                                            ? 'bg-primary/20 border-primary text-white'
                                            : 'bg-[#0a1628] border-white/10 text-muted-foreground hover:border-white/30'
                                    }`}
                                    data-testid="format-league"
                                >
                                    <Table className="w-6 h-6 mx-auto mb-2" />
                                    <p className="font-semibold text-sm">League</p>
                                    <p className="text-xs mt-1">Points table standings</p>
                                </button>
                            </div>
                            
                            {/* Format-specific options */}
                            {(format === 'round_robin' || format === 'league') && (
                                <div className="flex items-center gap-3 p-3 bg-[#0a1628] border border-white/10 mb-4">
                                    <input
                                        type="checkbox"
                                        id="homeAndAway"
                                        checked={homeAndAway}
                                        onChange={(e) => setHomeAndAway(e.target.checked)}
                                        className="w-4 h-4 accent-primary"
                                    />
                                    <label htmlFor="homeAndAway" className="text-sm">
                                        Home & Away (teams play twice)
                                    </label>
                                </div>
                            )}
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">Start Date</Label>
                                    <Input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="bg-[#0a1628] border-white/10"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">Matches Per Day</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={matchesPerDay}
                                        onChange={(e) => setMatchesPerDay(parseInt(e.target.value) || 4)}
                                        className="bg-[#0a1628] border-white/10"
                                    />
                                </div>
                            </div>
                            
                            <div className="mt-4 space-y-2">
                                <Label className="text-xs text-muted-foreground">Venue</Label>
                                <Input
                                    value={venue}
                                    onChange={(e) => setVenue(e.target.value)}
                                    placeholder="Main Stadium"
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                            
                            {/* Age Category Selection */}
                            <div className="mt-4 border-t border-white/10 pt-4">
                                <div className="flex items-center justify-between mb-3">
                                    <Label className="text-sm flex items-center gap-2 font-semibold">
                                        <Users className="w-4 h-4 text-primary" />
                                        Age Categories (Multi-Venue)
                                    </Label>
                                    <Switch
                                        checked={enableAgeCategoryScheduling}
                                        onCheckedChange={setEnableAgeCategoryScheduling}
                                    />
                                </div>
                                
                                {enableAgeCategoryScheduling && (
                                    <div className="space-y-3">
                                        {/* Add Age Category */}
                                        <div className="flex gap-2">
                                            <Select
                                                onValueChange={(value) => {
                                                    if (!selectedAgeCategories.includes(value)) {
                                                        setSelectedAgeCategories([...selectedAgeCategories, value]);
                                                        // Calculate next start time
                                                        const lastSchedule = ageCategorySchedules[ageCategorySchedules.length - 1];
                                                        const [h, m] = lastSchedule?.startTime?.split(':').map(Number) || [9, 0];
                                                        const totalMins = h * 60 + m + matchDuration + intervalBetweenMatches + extraTimeBuffer + 30;
                                                        const newH = Math.floor(totalMins / 60) % 24;
                                                        const newM = totalMins % 60;
                                                        const newTime = `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
                                                        setAgeCategorySchedules([...ageCategorySchedules, {
                                                            category: value,
                                                            startTime: newTime,
                                                            numVenues: 1,
                                                            venueNames: ['Pitch 1']
                                                        }]);
                                                    }
                                                }}
                                            >
                                                <SelectTrigger className="bg-[#0a1628] border-white/10 flex-1">
                                                    <SelectValue placeholder="+ Add Age Category" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-[#0d2847] border-white/10">
                                                    {AGE_CATEGORIES.filter(c => !selectedAgeCategories.includes(c)).map(cat => (
                                                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        
                                        {/* Age Category List with Multi-Venue Support */}
                                        <div className="space-y-4 max-h-[400px] overflow-y-auto">
                                            {ageCategorySchedules.map((schedule, idx) => (
                                                <div 
                                                    key={schedule.category}
                                                    className="p-3 bg-[#0a1628] border border-white/10 space-y-3"
                                                >
                                                    {/* Category Header */}
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className="text-sm font-bold text-primary bg-primary/20 px-3 py-1">
                                                                {schedule.category}
                                                            </div>
                                                            <Input
                                                                type="time"
                                                                value={schedule.startTime}
                                                                onChange={(e) => {
                                                                    const newSchedules = [...ageCategorySchedules];
                                                                    newSchedules[idx].startTime = e.target.value;
                                                                    setAgeCategorySchedules(newSchedules);
                                                                }}
                                                                className="bg-[#0d2847] border-white/10 h-8 w-28 text-sm"
                                                            />
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Label className="text-xs text-muted-foreground">Venues:</Label>
                                                            <Input
                                                                type="number"
                                                                min="1"
                                                                max="20"
                                                                value={schedule.numVenues || 1}
                                                                onChange={(e) => {
                                                                    const newNum = Math.min(20, parseInt(e.target.value) || 1);
                                                                    const newSchedules = [...ageCategorySchedules];
                                                                    newSchedules[idx].numVenues = newNum;
                                                                    // Expand venue names
                                                                    const currentNames = newSchedules[idx].venueNames || [];
                                                                    if (newNum > currentNames.length) {
                                                                        for (let i = currentNames.length; i < newNum; i++) {
                                                                            currentNames.push(`Pitch ${i + 1}`);
                                                                        }
                                                                    }
                                                                    newSchedules[idx].venueNames = currentNames;
                                                                    setAgeCategorySchedules(newSchedules);
                                                                }}
                                                                className="bg-[#0d2847] border-white/10 h-8 w-16 text-sm"
                                                            />
                                                            {selectedAgeCategories.length > 1 && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        setSelectedAgeCategories(selectedAgeCategories.filter(c => c !== schedule.category));
                                                                        setAgeCategorySchedules(ageCategorySchedules.filter(s => s.category !== schedule.category));
                                                                    }}
                                                                    className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Venue Names Grid */}
                                                    {(schedule.numVenues || 1) > 1 && (
                                                        <div className="space-y-2">
                                                            <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                                                <MapPin className="w-3 h-3" />
                                                                {schedule.numVenues} venues - {schedule.numVenues} matches run simultaneously
                                                            </Label>
                                                            <div className={`grid gap-1 ${(schedule.numVenues || 1) > 8 ? 'grid-cols-5' : (schedule.numVenues || 1) > 4 ? 'grid-cols-4' : 'grid-cols-2'}`}>
                                                                {Array.from({ length: schedule.numVenues || 1 }).map((_, vIdx) => (
                                                                    <Input
                                                                        key={vIdx}
                                                                        value={(schedule.venueNames || [])[vIdx] || `Pitch ${vIdx + 1}`}
                                                                        onChange={(e) => {
                                                                            const newSchedules = [...ageCategorySchedules];
                                                                            if (!newSchedules[idx].venueNames) {
                                                                                newSchedules[idx].venueNames = [];
                                                                            }
                                                                            newSchedules[idx].venueNames[vIdx] = e.target.value;
                                                                            setAgeCategorySchedules(newSchedules);
                                                                        }}
                                                                        placeholder={`Pitch ${vIdx + 1}`}
                                                                        className="bg-[#0d2847] border-white/10 h-7 text-xs"
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Single venue input when numVenues = 1 */}
                                                    {(schedule.numVenues || 1) === 1 && (
                                                        <Input
                                                            value={(schedule.venueNames || [])[0] || 'Main Venue'}
                                                            onChange={(e) => {
                                                                const newSchedules = [...ageCategorySchedules];
                                                                newSchedules[idx].venueNames = [e.target.value];
                                                                setAgeCategorySchedules(newSchedules);
                                                            }}
                                                            placeholder="Venue name"
                                                            className="bg-[#0d2847] border-white/10 h-8 text-sm"
                                                        />
                                                    )}
                                                    
                                                    {/* Preview for this category */}
                                                    <div className="text-xs text-muted-foreground bg-[#0d2847] p-2 border border-white/5">
                                                        {(schedule.numVenues || 1) > 1 ? (
                                                            <span>
                                                                All {schedule.numVenues} matches start at <span className="text-primary font-semibold">{schedule.startTime}</span> on different pitches
                                                            </span>
                                                        ) : (
                                                            <span>
                                                                Matches at <span className="text-primary font-semibold">{schedule.startTime}</span> @ {(schedule.venueNames || [])[0] || 'Main Venue'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        
                                        <p className="text-xs text-muted-foreground">
                                            Example: U9 with 40 teams, 20 venues = 20 matches at same time
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Auto Time Scheduling Configuration */}
                        <div className="bg-[#0d2847] border border-white/5 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-heading font-bold text-lg uppercase flex items-center gap-2">
                                    <Clock className="w-5 h-5 text-primary" />
                                    Auto Time Scheduling
                                </h3>
                                <div className="flex items-center gap-2">
                                    <Label className="text-xs text-muted-foreground">Enable</Label>
                                    <Switch
                                        checked={enableTimeScheduling}
                                        onCheckedChange={setEnableTimeScheduling}
                                    />
                                </div>
                            </div>
                            
                            {enableTimeScheduling && (
                                <div className="space-y-4">
                                    {/* Start Time & Duration */}
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-xs text-muted-foreground">Start Time</Label>
                                            <Input
                                                type="time"
                                                value={startTime}
                                                onChange={(e) => setStartTime(e.target.value)}
                                                className="bg-[#0a1628] border-white/10"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-xs text-muted-foreground">Match Duration (min)</Label>
                                            <Input
                                                type="number"
                                                min="10"
                                                max="180"
                                                value={matchDuration}
                                                onChange={(e) => setMatchDuration(parseInt(e.target.value) || 90)}
                                                className="bg-[#0a1628] border-white/10"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-xs text-muted-foreground">Interval (min)</Label>
                                            <Input
                                                type="number"
                                                min="5"
                                                max="60"
                                                value={intervalBetweenMatches}
                                                onChange={(e) => setIntervalBetweenMatches(parseInt(e.target.value) || 15)}
                                                className="bg-[#0a1628] border-white/10"
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Extra Time & Venues */}
                                    <div className="grid grid-cols-2 gap-4">
                                        {format === 'knockout' && (
                                            <div className="space-y-2">
                                                <Label className="text-xs text-muted-foreground">Extra Time Buffer (min)</Label>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    max="60"
                                                    value={extraTimeBuffer}
                                                    onChange={(e) => setExtraTimeBuffer(parseInt(e.target.value) || 30)}
                                                    className="bg-[#0a1628] border-white/10"
                                                />
                                            </div>
                                        )}
                                        <div className="space-y-2">
                                            <Label className="text-xs text-muted-foreground">Number of Venues (Parallel)</Label>
                                            <Input
                                                type="number"
                                                min="1"
                                                max="20"
                                                value={numVenues}
                                                onChange={(e) => {
                                                    const newNum = Math.min(20, parseInt(e.target.value) || 1);
                                                    setNumVenues(newNum);
                                                    // Expand venue names array if needed
                                                    if (newNum > venueNames.length) {
                                                        const newNames = [...venueNames];
                                                        for (let i = venueNames.length; i < newNum; i++) {
                                                            newNames.push(`Pitch ${i + 1}`);
                                                        }
                                                        setVenueNames(newNames);
                                                    }
                                                }}
                                                className="bg-[#0a1628] border-white/10"
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Venue Names (if multiple) */}
                                    {numVenues > 1 && (
                                        <div className="space-y-2">
                                            <Label className="text-xs text-muted-foreground flex items-center gap-2">
                                                <MapPin className="w-3 h-3" />
                                                Venue Names ({numVenues} venues - matches run in parallel)
                                            </Label>
                                            <div className={`grid gap-2 max-h-[200px] overflow-y-auto ${numVenues > 6 ? 'grid-cols-4' : numVenues > 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                                                {Array.from({ length: numVenues }).map((_, idx) => (
                                                    <Input
                                                        key={idx}
                                                        value={venueNames[idx] || `Pitch ${idx + 1}`}
                                                        onChange={(e) => {
                                                            const newNames = [...venueNames];
                                                            newNames[idx] = e.target.value;
                                                            setVenueNames(newNames);
                                                        }}
                                                        placeholder={`Pitch ${idx + 1}`}
                                                        className="bg-[#0a1628] border-white/10 text-xs h-8"
                                                    />
                                                ))}
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                With {numVenues} venues, up to {numVenues} matches can run simultaneously
                                            </p>
                                        </div>
                                    )}
                                    
                                    {/* Break Configuration */}
                                    <div className="border-t border-white/10 pt-4 mt-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <Label className="text-sm flex items-center gap-2">
                                                <Coffee className="w-4 h-4 text-yellow-400" />
                                                Break Period
                                            </Label>
                                            <Switch
                                                checked={enableBreaks}
                                                onCheckedChange={setEnableBreaks}
                                            />
                                        </div>
                                        
                                        {enableBreaks && (
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label className="text-xs text-muted-foreground">Break Start Time</Label>
                                                    <Input
                                                        type="time"
                                                        value={breakStartTime}
                                                        onChange={(e) => setBreakStartTime(e.target.value)}
                                                        className="bg-[#0a1628] border-white/10"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-xs text-muted-foreground">Break Duration (min)</Label>
                                                    <Input
                                                        type="number"
                                                        min="15"
                                                        max="120"
                                                        value={breakDuration}
                                                        onChange={(e) => setBreakDuration(parseInt(e.target.value) || 60)}
                                                        className="bg-[#0a1628] border-white/10"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Time Preview */}
                                    <div className="bg-[#0a1628] p-3 border border-white/10 text-xs">
                                        <p className="font-semibold text-white mb-1">Schedule Preview:</p>
                                        <p className="text-muted-foreground">
                                            Match 1: {startTime} 
                                            {numVenues > 1 && ` (${venueNames[0] || 'Venue 1'})`}
                                        </p>
                                        <p className="text-muted-foreground">
                                            Match 2: {(() => {
                                                const [h, m] = startTime.split(':').map(Number);
                                                const totalMins = h * 60 + m + (numVenues > 1 ? 0 : matchDuration + intervalBetweenMatches + (format === 'knockout' ? extraTimeBuffer : 0));
                                                const newH = Math.floor(totalMins / 60) % 24;
                                                const newM = totalMins % 60;
                                                return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
                                            })()}
                                            {numVenues > 1 && ` (${venueNames[1] || 'Venue 2'})`}
                                        </p>
                                        {enableBreaks && (
                                            <p className="text-yellow-400 mt-1">
                                                Break: {breakStartTime} - {(() => {
                                                    const [h, m] = breakStartTime.split(':').map(Number);
                                                    const totalMins = h * 60 + m + breakDuration;
                                                    const newH = Math.floor(totalMins / 60) % 24;
                                                    const newM = totalMins % 60;
                                                    return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
                                                })()}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Generate Button */}
                        <Button
                            onClick={() => generateBracket(false)}
                            disabled={generating || seededTeams.length < 2}
                            className="w-full bg-primary hover:bg-cyan-400 text-[#0a1628] h-12 font-bold uppercase"
                            data-testid="generate-bracket-btn"
                        >
                            {generating ? (
                                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                            ) : (
                                <Play className="w-5 h-5 mr-2" />
                            )}
                            Generate {format.replace('_', ' ')} Bracket
                        </Button>

                        {/* Existing Bracket Info */}
                        {bracket && bracket.total_matches > 0 && (
                            <div className="bg-green-500/10 border border-green-500/30 p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle className="w-5 h-5 text-green-400" />
                                        <div>
                                            <p className="font-semibold text-green-400">Bracket Generated</p>
                                            <p className="text-xs text-muted-foreground">
                                                {bracket.total_matches} matches • {bracket.format} format
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowPreviewDialog(true)}
                                        className="border-green-500/30 text-green-400"
                                    >
                                        <Eye className="w-4 h-4 mr-1" />
                                        View
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
              </TabsContent>
              
              {/* Individual Sports Tab */}
              <TabsContent value="individual">
                <div className="space-y-6">
                    {/* Mode Selector: Single Event vs Multi-Discipline */}
                    <Tabs defaultValue="multi" className="w-full">
                        <TabsList className="bg-[#0a1628] border border-white/10 p-1 mb-4">
                            <TabsTrigger value="single" className="data-[state=active]:bg-primary data-[state=active]:text-[#0a1628]">
                                Single Event
                            </TabsTrigger>
                            <TabsTrigger value="multi" className="data-[state=active]:bg-primary data-[state=active]:text-[#0a1628]">
                                Multi-Discipline Schedule
                            </TabsTrigger>
                        </TabsList>
                        
                        {/* Single Event Mode */}
                        <TabsContent value="single">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Left: Athlete Seeding Panel */}
                                <div className="bg-[#0d2847] border border-white/5 p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="font-heading font-bold text-lg uppercase flex items-center gap-2">
                                            <User className="w-5 h-5 text-primary" />
                                            Athlete Seedings
                                        </h3>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={addManualAthlete}
                                                className="border-white/10"
                                            >
                                                <Plus className="w-4 h-4 mr-1" />
                                                Add
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={randomizeAthleteSeeds}
                                                className="border-white/10"
                                            >
                                                <Shuffle className="w-4 h-4 mr-1" />
                                                Randomize
                                            </Button>
                                        </div>
                                    </div>
                                    
                                    {loading ? (
                                        <div className="flex items-center justify-center py-8">
                                            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                        </div>
                                    ) : seededAthletes.length > 0 ? (
                                        <div className="space-y-2 max-h-[350px] overflow-y-auto">
                                            {seededAthletes.map((athlete, index) => (
                                                <div 
                                                    key={athlete.athlete_id}
                                                    className="flex items-center gap-3 p-3 bg-[#0a1628] border border-white/10 hover:border-primary/30"
                                                >
                                                    <div className="w-8 h-8 bg-primary/20 flex items-center justify-center font-bold text-primary text-sm">
                                                        {athlete.seed}
                                                    </div>
                                                    <div className="flex-1 grid grid-cols-2 gap-2">
                                                        <Input
                                                            value={athlete.athlete_name}
                                                            onChange={(e) => updateAthleteName(index, e.target.value)}
                                                            className="bg-[#0d2847] border-white/10 h-8 text-sm"
                                                            placeholder="Athlete name"
                                                        />
                                                        <Input
                                                            value={athlete.personal_best || ''}
                                                            onChange={(e) => updateAthletePB(index, e.target.value)}
                                                            className="bg-[#0d2847] border-white/10 h-8 text-sm"
                                                            placeholder="PB (e.g., 10.45)"
                                                        />
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => moveAthleteSeed(index, -1)}
                                                            disabled={index === 0}
                                                            className="h-8 w-8 p-0"
                                                        >
                                                            <ArrowUp className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => moveAthleteSeed(index, 1)}
                                                            disabled={index === seededAthletes.length - 1}
                                                            className="h-8 w-8 p-0"
                                                        >
                                                            <ArrowDown className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => removeAthlete(index)}
                                                            className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8">
                                            <User className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                                            <p className="text-muted-foreground text-sm">No registered athletes</p>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={addManualAthlete}
                                                className="mt-3 border-white/10"
                                            >
                                                <Plus className="w-4 h-4 mr-1" />
                                                Add Athletes Manually
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Right: Configuration Panel */}
                                <div className="bg-[#0d2847] border border-white/5 p-6 space-y-6">
                                    {/* Event Info */}
                                    <div>
                                        <Label className="text-xs text-muted-foreground mb-2 block">Event Name</Label>
                                        <Input
                                            value={eventName}
                                            onChange={(e) => setEventName(e.target.value)}
                                            placeholder="e.g., Men's 100m Freestyle"
                                            className="bg-[#0a1628] border-white/10"
                                        />
                                    </div>
                                    
                                    {/* Sport Type */}
                                    <div>
                                        <Label className="text-xs text-muted-foreground mb-2 block">Sport Type</Label>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { id: 'swimming', icon: Waves, label: 'Swimming' },
                                                { id: 'athletics', icon: Activity, label: 'Athletics' },
                                                { id: 'cycling', icon: Timer, label: 'Cycling' }
                                            ].map(sport => (
                                                <Button
                                                    key={sport.id}
                                                    variant={sportType === sport.id ? "default" : "outline"}
                                                    onClick={() => setSportType(sport.id)}
                                                    className={`h-16 flex-col gap-1 ${
                                                        sportType === sport.id 
                                                            ? 'bg-primary text-[#0a1628]' 
                                                            : 'border-white/10 hover:border-primary/50'
                                                    }`}
                                                >
                                                    <sport.icon className="w-5 h-5" />
                                                    <span className="text-xs">{sport.label}</span>
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Format */}
                                    <div>
                                        <Label className="text-xs text-muted-foreground mb-2 block">Competition Format</Label>
                                        <Select value={individualFormat} onValueChange={setIndividualFormat}>
                                            <SelectTrigger className="bg-[#0a1628] border-white/10">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#0d2847] border-white/10">
                                                <SelectItem value="heats_finals">Heats → Finals</SelectItem>
                                                <SelectItem value="heats_semis_finals">Heats → Semi-Finals → Finals</SelectItem>
                                                <SelectItem value="time_trials">Time Trials (No Heats)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    
                                    {/* Heat Configuration */}
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <Label className="text-xs text-muted-foreground">Athletes/Heat</Label>
                                            <Input
                                                type="number"
                                                min="4"
                                                max="10"
                                                value={athletesPerHeat}
                                                onChange={(e) => setAthletesPerHeat(parseInt(e.target.value) || 8)}
                                                className="bg-[#0a1628] border-white/10"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-muted-foreground">Auto Qualify</Label>
                                            <Input
                                                type="number"
                                                min="1"
                                                max="4"
                                                value={qualifiersPerHeat}
                                                onChange={(e) => setQualifiersPerHeat(parseInt(e.target.value) || 2)}
                                                className="bg-[#0a1628] border-white/10"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-muted-foreground">Time Qualify</Label>
                                            <Input
                                                type="number"
                                                min="0"
                                                max="4"
                                                value={timeQualifiers}
                                                onChange={(e) => setTimeQualifiers(parseInt(e.target.value) || 2)}
                                                className="bg-[#0a1628] border-white/10"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="bg-[#0a1628] p-3 border border-white/10 text-xs text-muted-foreground">
                                        <p className="font-semibold text-white mb-1">Qualification Rule:</p>
                                        <p>Top {qualifiersPerHeat} per heat + {timeQualifiers} fastest times advance</p>
                                    </div>
                                    
                                    {/* Age Category Selection for Individual Sports */}
                                    <div className="border-t border-white/10 pt-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <Label className="text-sm flex items-center gap-2 font-semibold">
                                                <Users className="w-4 h-4 text-primary" />
                                                Age Categories (Multi-Venue)
                                            </Label>
                                            <Switch
                                                checked={enableAgeCategoryScheduling}
                                                onCheckedChange={setEnableAgeCategoryScheduling}
                                            />
                                        </div>
                                        
                                        {enableAgeCategoryScheduling && (
                                            <div className="space-y-3">
                                                <div className="flex gap-2">
                                                    <Select
                                                        onValueChange={(value) => {
                                                            if (!selectedAgeCategories.includes(value)) {
                                                                setSelectedAgeCategories([...selectedAgeCategories, value]);
                                                                const lastSchedule = ageCategorySchedules[ageCategorySchedules.length - 1];
                                                                const [h, m] = lastSchedule?.startTime?.split(':').map(Number) || [9, 0];
                                                                const totalMins = h * 60 + m + heatDuration + intervalBetweenHeats + 30;
                                                                const newH = Math.floor(totalMins / 60) % 24;
                                                                const newM = totalMins % 60;
                                                                const newTime = `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
                                                                setAgeCategorySchedules([...ageCategorySchedules, {
                                                                    category: value,
                                                                    startTime: newTime,
                                                                    numVenues: 1,
                                                                    venueNames: ['Lane 1']
                                                                }]);
                                                            }
                                                        }}
                                                    >
                                                        <SelectTrigger className="bg-[#0a1628] border-white/10 flex-1">
                                                            <SelectValue placeholder="+ Add Age Category" />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-[#0d2847] border-white/10">
                                                            {AGE_CATEGORIES.filter(c => !selectedAgeCategories.includes(c)).map(cat => (
                                                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                
                                                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                                                    {ageCategorySchedules.map((schedule, idx) => (
                                                        <div 
                                                            key={schedule.category}
                                                            className="p-2 bg-[#0a1628] border border-white/10 space-y-2"
                                                        >
                                                            {/* Header */}
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs font-bold text-primary bg-primary/20 px-2 py-0.5">
                                                                        {schedule.category}
                                                                    </span>
                                                                    <Input
                                                                        type="time"
                                                                        value={schedule.startTime}
                                                                        onChange={(e) => {
                                                                            const newSchedules = [...ageCategorySchedules];
                                                                            newSchedules[idx].startTime = e.target.value;
                                                                            setAgeCategorySchedules(newSchedules);
                                                                        }}
                                                                        className="bg-[#0d2847] border-white/10 h-7 w-24 text-xs"
                                                                    />
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <Label className="text-xs text-muted-foreground">Lanes:</Label>
                                                                    <Input
                                                                        type="number"
                                                                        min="1"
                                                                        max="20"
                                                                        value={schedule.numVenues || 1}
                                                                        onChange={(e) => {
                                                                            const newNum = Math.min(20, parseInt(e.target.value) || 1);
                                                                            const newSchedules = [...ageCategorySchedules];
                                                                            newSchedules[idx].numVenues = newNum;
                                                                            const currentNames = newSchedules[idx].venueNames || [];
                                                                            if (newNum > currentNames.length) {
                                                                                for (let i = currentNames.length; i < newNum; i++) {
                                                                                    currentNames.push(`Lane ${i + 1}`);
                                                                                }
                                                                            }
                                                                            newSchedules[idx].venueNames = currentNames;
                                                                            setAgeCategorySchedules(newSchedules);
                                                                        }}
                                                                        className="bg-[#0d2847] border-white/10 h-7 w-14 text-xs"
                                                                    />
                                                                    {selectedAgeCategories.length > 1 && (
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => {
                                                                                setSelectedAgeCategories(selectedAgeCategories.filter(c => c !== schedule.category));
                                                                                setAgeCategorySchedules(ageCategorySchedules.filter(s => s.category !== schedule.category));
                                                                            }}
                                                                            className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                                                                        >
                                                                            <Trash2 className="w-3 h-3" />
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Multi-venue info */}
                                                            {(schedule.numVenues || 1) > 1 && (
                                                                <p className="text-xs text-muted-foreground">
                                                                    {schedule.numVenues} heats run simultaneously at {schedule.startTime}
                                                                </p>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                                
                                                <p className="text-xs text-muted-foreground">
                                                    E.g., U9 100m with 80 athletes, 10 lanes = 10 heats at same time
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Date and Venue */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label className="text-xs text-muted-foreground">Start Date</Label>
                                            <Input
                                                type="date"
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                                className="bg-[#0a1628] border-white/10"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-muted-foreground">Venue</Label>
                                            <Input
                                                value={venue}
                                                onChange={(e) => setVenue(e.target.value)}
                                                placeholder="Olympic Pool"
                                                className="bg-[#0a1628] border-white/10"
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Time Scheduling for Single Event */}
                                    <div className="border-t border-white/10 pt-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <Label className="text-sm flex items-center gap-2 font-semibold">
                                                <Clock className="w-4 h-4 text-primary" />
                                                Time & Venue Scheduling
                                            </Label>
                                        </div>
                                        
                                        <div className="space-y-3">
                                            <div className="grid grid-cols-4 gap-3">
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-muted-foreground">Start Time</Label>
                                                    <Input
                                                        type="time"
                                                        value={individualStartTime}
                                                        onChange={(e) => setIndividualStartTime(e.target.value)}
                                                        className="bg-[#0a1628] border-white/10"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-muted-foreground">Heat Duration (min)</Label>
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        max="60"
                                                        value={heatDuration}
                                                        onChange={(e) => setHeatDuration(parseInt(e.target.value) || 5)}
                                                        className="bg-[#0a1628] border-white/10"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-muted-foreground">Interval (min)</Label>
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        max="30"
                                                        value={intervalBetweenHeats}
                                                        onChange={(e) => setIntervalBetweenHeats(parseInt(e.target.value) || 10)}
                                                        className="bg-[#0a1628] border-white/10"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-muted-foreground">Num. Venues/Lanes</Label>
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        max="20"
                                                        value={numVenues}
                                                        onChange={(e) => {
                                                            const newNum = Math.min(20, parseInt(e.target.value) || 1);
                                                            setNumVenues(newNum);
                                                            if (newNum > venueNames.length) {
                                                                const newNames = [...venueNames];
                                                                for (let i = venueNames.length; i < newNum; i++) {
                                                                    newNames.push(`Lane ${i + 1}`);
                                                                }
                                                                setVenueNames(newNames);
                                                            }
                                                        }}
                                                        className="bg-[#0a1628] border-white/10"
                                                    />
                                                </div>
                                            </div>
                                            
                                            {/* Multiple Venue Names */}
                                            {numVenues > 1 && (
                                                <div className="space-y-2">
                                                    <Label className="text-xs text-muted-foreground flex items-center gap-2">
                                                        <MapPin className="w-3 h-3 text-primary" />
                                                        Venue/Lane Names ({numVenues} parallel)
                                                    </Label>
                                                    <div className={`grid gap-2 max-h-[150px] overflow-y-auto ${numVenues > 6 ? 'grid-cols-4' : numVenues > 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                                                        {Array.from({ length: numVenues }).map((_, idx) => (
                                                            <Input
                                                                key={idx}
                                                                value={venueNames[idx] || `Lane ${idx + 1}`}
                                                                onChange={(e) => {
                                                                    const newNames = [...venueNames];
                                                                    newNames[idx] = e.target.value;
                                                                    setVenueNames(newNames);
                                                                }}
                                                                placeholder={`Lane ${idx + 1}`}
                                                                className="bg-[#0a1628] border-white/10 text-xs h-7"
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Break Option */}
                                            <div className="flex items-center justify-between bg-[#0a1628] p-2 border border-white/10">
                                                <Label className="text-xs flex items-center gap-2 text-muted-foreground">
                                                    <Coffee className="w-3 h-3 text-yellow-400" />
                                                    Break Period
                                                </Label>
                                                <Switch
                                                    checked={enableIndividualBreaks}
                                                    onCheckedChange={setEnableIndividualBreaks}
                                                />
                                            </div>
                                            
                                            {enableIndividualBreaks && (
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div className="space-y-1">
                                                        <Label className="text-xs text-muted-foreground">Break Start</Label>
                                                        <Input
                                                            type="time"
                                                            value={individualBreakStartTime}
                                                            onChange={(e) => setIndividualBreakStartTime(e.target.value)}
                                                            className="bg-[#0a1628] border-white/10"
                                                        />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <Label className="text-xs text-muted-foreground">Duration (min)</Label>
                                                        <Input
                                                            type="number"
                                                            min="15"
                                                            max="120"
                                                            value={individualBreakDuration}
                                                            onChange={(e) => setIndividualBreakDuration(parseInt(e.target.value) || 60)}
                                                            className="bg-[#0a1628] border-white/10"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Schedule Preview */}
                                            <div className="bg-[#0a1628] p-2 border border-white/10 text-xs">
                                                <p className="font-semibold text-white mb-1">Schedule Preview:</p>
                                                <p className="text-muted-foreground">
                                                    Heat 1: {individualStartTime} | Heat 2: {(() => {
                                                        const [h, m] = individualStartTime.split(':').map(Number);
                                                        const totalMins = h * 60 + m + heatDuration + intervalBetweenHeats;
                                                        const newH = Math.floor(totalMins / 60) % 24;
                                                        const newM = totalMins % 60;
                                                        return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
                                                    })()} | Heat 3: {(() => {
                                                        const [h, m] = individualStartTime.split(':').map(Number);
                                                        const totalMins = h * 60 + m + 2 * (heatDuration + intervalBetweenHeats);
                                                        const newH = Math.floor(totalMins / 60) % 24;
                                                        const newM = totalMins % 60;
                                                        return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
                                                    })()}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Generate Button */}
                                    <Button
                                        onClick={() => generateIndividualHeats(false)}
                                        disabled={generating || seededAthletes.length < 2 || !eventName.trim()}
                                        className="w-full bg-primary hover:bg-cyan-400 text-[#0a1628] h-12 font-bold uppercase"
                                    >
                                        {generating ? (
                                            <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                                        ) : (
                                            <Play className="w-5 h-5 mr-2" />
                                        )}
                                        Generate Heats & Finals
                                    </Button>
                                </div>
                            </div>
                        </TabsContent>
                        
                        {/* Multi-Discipline Mode */}
                        <TabsContent value="multi">
                            <div className="space-y-6">
                                {/* Header with Add Discipline Button */}
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-heading font-bold text-lg uppercase flex items-center gap-2">
                                            <Calendar className="w-5 h-5 text-primary" />
                                            Multi-Discipline Schedule
                                        </h3>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Schedule multiple events with different start times and venues
                                        </p>
                                    </div>
                                    <Button
                                        onClick={() => setShowAddDisciplineDialog(true)}
                                        className="bg-primary hover:bg-cyan-400 text-[#0a1628]"
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Add Discipline
                                    </Button>
                                </div>
                                
                                {/* Global Settings */}
                                <div className="grid grid-cols-4 gap-4 bg-[#0d2847] border border-white/5 p-4">
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Start Date</Label>
                                        <Input
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            className="bg-[#0a1628] border-white/10"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Sport Type</Label>
                                        <Select value={sportType} onValueChange={setSportType}>
                                            <SelectTrigger className="bg-[#0a1628] border-white/10">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#0d2847] border-white/10">
                                                <SelectItem value="athletics">Athletics</SelectItem>
                                                <SelectItem value="swimming">Swimming</SelectItem>
                                                <SelectItem value="cycling">Cycling</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            checked={enableIndividualBreaks}
                                            onCheckedChange={setEnableIndividualBreaks}
                                        />
                                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Coffee className="w-3 h-3 text-yellow-400" />
                                            Break
                                        </Label>
                                    </div>
                                    {enableIndividualBreaks && (
                                        <div className="grid grid-cols-2 gap-2">
                                            <Input
                                                type="time"
                                                value={individualBreakStartTime}
                                                onChange={(e) => setIndividualBreakStartTime(e.target.value)}
                                                className="bg-[#0a1628] border-white/10 text-sm"
                                                title="Break Start"
                                            />
                                            <Input
                                                type="number"
                                                value={individualBreakDuration}
                                                onChange={(e) => setIndividualBreakDuration(parseInt(e.target.value) || 60)}
                                                className="bg-[#0a1628] border-white/10 text-sm"
                                                placeholder="min"
                                                title="Break Duration"
                                            />
                                        </div>
                                    )}
                                </div>
                                
                                {/* Scheduled Disciplines List */}
                                {scheduledDisciplines.length > 0 ? (
                                    <div className="space-y-3">
                                        {scheduledDisciplines.map((discipline, idx) => (
                                            <div 
                                                key={discipline.id}
                                                className="bg-[#0d2847] border border-white/5 p-4"
                                            >
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 bg-primary/20 flex items-center justify-center font-bold text-primary text-sm">
                                                            {idx + 1}
                                                        </div>
                                                        <div>
                                                            <h4 className="font-bold text-white">
                                                                {discipline.ageCategory && discipline.ageCategory !== 'Open' && (
                                                                    <span className="text-yellow-400 mr-1">{discipline.ageCategory}</span>
                                                                )}
                                                                {discipline.name}
                                                                {(discipline.numVenues || 1) > 1 && (
                                                                    <span className="text-cyan-400 ml-2 text-xs">({discipline.numVenues} venues)</span>
                                                                )}
                                                            </h4>
                                                            <p className="text-xs text-muted-foreground">
                                                                {discipline.athletes?.length || seededAthletes.length} athletes
                                                                {(discipline.numVenues || 1) > 1 && ` • ${discipline.numVenues} heats simultaneously`}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {/* Number of Venues control */}
                                                        <div className="flex items-center gap-1">
                                                            <Label className="text-xs text-muted-foreground">Venues:</Label>
                                                            <Input
                                                                type="number"
                                                                min="1"
                                                                max="20"
                                                                value={discipline.numVenues || 1}
                                                                onChange={(e) => {
                                                                    const newNum = Math.min(20, parseInt(e.target.value) || 1);
                                                                    updateDiscipline(discipline.id, 'numVenues', newNum);
                                                                }}
                                                                className="bg-[#0a1628] border-white/10 h-8 w-14 text-sm"
                                                            />
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => removeDiscipline(discipline.id)}
                                                            className="text-red-400 hover:text-red-300"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                                
                                                <div className="grid grid-cols-5 gap-3">
                                                    <div>
                                                        <Label className="text-xs text-muted-foreground">Venue Type</Label>
                                                        <Select 
                                                            value={discipline.venue}
                                                            onValueChange={(v) => updateDiscipline(discipline.id, 'venue', v)}
                                                        >
                                                            <SelectTrigger className="bg-[#0a1628] border-white/10 h-9">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent className="bg-[#0d2847] border-white/10">
                                                                {individualVenues.map(v => (
                                                                    <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>
                                                                ))}
                                                                <SelectItem value="custom">Custom...</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs text-muted-foreground">Start Time</Label>
                                                        <Input
                                                            type="time"
                                                            value={discipline.startTime}
                                                            onChange={(e) => updateDiscipline(discipline.id, 'startTime', e.target.value)}
                                                            className="bg-[#0a1628] border-white/10 h-9"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs text-muted-foreground">Duration (min)</Label>
                                                        <Input
                                                            type="number"
                                                            min="1"
                                                            max="120"
                                                            value={discipline.duration}
                                                            onChange={(e) => updateDiscipline(discipline.id, 'duration', parseInt(e.target.value) || 10)}
                                                            className="bg-[#0a1628] border-white/10 h-9"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs text-muted-foreground">Interval (min)</Label>
                                                        <Input
                                                            type="number"
                                                            min="1"
                                                            max="30"
                                                            value={discipline.interval}
                                                            onChange={(e) => updateDiscipline(discipline.id, 'interval', parseInt(e.target.value) || 5)}
                                                            className="bg-[#0a1628] border-white/10 h-9"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs text-muted-foreground">Athletes/Heat</Label>
                                                        <Input
                                                            type="number"
                                                            min="4"
                                                            max="10"
                                                            value={discipline.athletesPerHeat}
                                                            onChange={(e) => updateDiscipline(discipline.id, 'athletesPerHeat', parseInt(e.target.value) || 8)}
                                                            className="bg-[#0a1628] border-white/10 h-9"
                                                        />
                                                    </div>
                                                </div>
                                                
                                                {/* Time Preview */}
                                                <div className="mt-2 text-xs text-muted-foreground bg-[#0a1628] p-2 border border-white/10">
                                                    <span className="text-primary font-semibold">{discipline.startTime}</span>
                                                    <span className="mx-2">→</span>
                                                    <span>{calculateScheduleEndTime(
                                                        discipline.startTime, 
                                                        discipline.duration, 
                                                        discipline.interval,
                                                        Math.ceil((discipline.athletes?.length || seededAthletes.length) / discipline.athletesPerHeat)
                                                    )}</span>
                                                    <span className="ml-2 text-yellow-400">@ {discipline.venue}</span>
                                                </div>
                                            </div>
                                        ))}
                                        
                                        {/* Generate Schedule Button */}
                                        <Button
                                            onClick={generateMultiDisciplineSchedule}
                                            disabled={generating || scheduledDisciplines.length === 0}
                                            className="w-full bg-primary hover:bg-cyan-400 text-[#0a1628] h-12 font-bold uppercase mt-4"
                                        >
                                            {generating ? (
                                                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                                            ) : (
                                                <Play className="w-5 h-5 mr-2" />
                                            )}
                                            Generate Full Schedule ({scheduledDisciplines.length} disciplines)
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="text-center py-12 bg-[#0d2847] border border-white/5">
                                        <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                                        <p className="text-lg font-semibold text-white mb-2">No Disciplines Added</p>
                                        <p className="text-sm text-muted-foreground mb-4">
                                            Add disciplines to create a parallel schedule<br/>
                                            (e.g., 100m race + High Jump + Shot Put at same time)
                                        </p>
                                        <Button
                                            onClick={() => setShowAddDisciplineDialog(true)}
                                            className="bg-primary hover:bg-cyan-400 text-[#0a1628]"
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Add First Discipline
                                        </Button>
                                    </div>
                                )}
                                
                                {/* Visual Schedule Timeline */}
                                {scheduledDisciplines.length > 1 && (
                                    <div className="bg-[#0d2847] border border-white/5 p-4">
                                        <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                                            <Clock className="w-4 h-4 text-primary" />
                                            Schedule Overview
                                        </h4>
                                        <div className="space-y-2">
                                            {[...new Set(scheduledDisciplines.map(d => d.venue))].map(venueName => (
                                                <div key={venueName} className="flex items-center gap-3">
                                                    <div className="w-28 text-xs text-muted-foreground font-semibold">
                                                        {venueName}
                                                    </div>
                                                    <div className="flex-1 flex gap-1 bg-[#0a1628] p-1 rounded">
                                                        {scheduledDisciplines
                                                            .filter(d => d.venue === venueName)
                                                            .sort((a, b) => a.startTime.localeCompare(b.startTime))
                                                            .map(d => (
                                                                <div 
                                                                    key={d.id}
                                                                    className="bg-primary/20 border border-primary/50 px-2 py-1 text-xs rounded"
                                                                    title={`${d.name}: ${d.startTime} - ${calculateScheduleEndTime(d.startTime, d.duration, d.interval, 3)}`}
                                                                >
                                                                    <span className="text-primary font-semibold">{d.startTime}</span>
                                                                    <span className="text-white ml-1">{d.name.split(' ')[0]}</span>
                                                                </div>
                                                            ))
                                                        }
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>
              </TabsContent>
            </Tabs>
            )}

            {/* Bracket Preview Dialog */}
            <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-4xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl uppercase flex items-center gap-2">
                            <FormatIcon className="w-5 h-5 text-primary" />
                            {format.replace('_', ' ')} Bracket - {selectedTournament?.name}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {bracket && (
                        <div className="space-y-6 py-4">
                            {/* Summary */}
                            <div className="grid grid-cols-3 gap-4">
                                <div className="bg-[#0a1628] p-4 border border-white/10 text-center">
                                    <p className="text-2xl font-bold text-primary">{bracket.total_matches}</p>
                                    <p className="text-xs text-muted-foreground">Total Matches</p>
                                </div>
                                <div className="bg-[#0a1628] p-4 border border-white/10 text-center">
                                    <p className="text-2xl font-bold text-cyan-400">{bracket.rounds?.length || 0}</p>
                                    <p className="text-xs text-muted-foreground">Rounds</p>
                                </div>
                                <div className="bg-[#0a1628] p-4 border border-white/10 text-center">
                                    <p className="text-2xl font-bold text-green-400">{seededTeams.length}</p>
                                    <p className="text-xs text-muted-foreground">Teams</p>
                                </div>
                            </div>
                            
                            {/* Rounds */}
                            <div className="space-y-4">
                                {bracket.rounds?.map((round, idx) => (
                                    <div key={idx} className="bg-[#0a1628] border border-white/10 p-4">
                                        <h4 className="font-semibold text-primary mb-3 flex items-center gap-2">
                                            <ChevronRight className="w-4 h-4" />
                                            {round.name}
                                            <span className="text-xs text-muted-foreground ml-2">
                                                ({round.matches?.length} matches)
                                            </span>
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {round.matches?.map((match, mIdx) => (
                                                <div 
                                                    key={mIdx} 
                                                    className="flex items-center justify-between p-2 bg-[#0d2847] border border-white/5 hover:border-primary/30 transition-colors"
                                                >
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium">
                                                            {match.home_team} 
                                                            <span className="text-primary mx-2">
                                                                {(match.home_score || match.score1 || 0)} - {(match.away_score || match.score2 || 0)}
                                                            </span>
                                                            {match.away_team}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {match.date || match.scheduled_date} • {match.time || match.scheduled_time}
                                                        </p>
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleEditMatch(match)}
                                                        className="h-8 w-8 p-0 hover:bg-primary/20"
                                                        data-testid={`edit-match-${idx}-${mIdx}`}
                                                    >
                                                        <Edit className="w-4 h-4 text-muted-foreground hover:text-primary" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {/* League Standings Preview */}
                            {bracket.standings && (
                                <div className="bg-[#0a1628] border border-white/10 p-4">
                                    <h4 className="font-semibold text-primary mb-3 flex items-center gap-2">
                                        <Table className="w-4 h-4" />
                                        Initial League Standings
                                    </h4>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-white/10">
                                                    <th className="text-left py-2 px-2">#</th>
                                                    <th className="text-left py-2 px-2">Team</th>
                                                    <th className="text-center py-2 px-2">P</th>
                                                    <th className="text-center py-2 px-2">W</th>
                                                    <th className="text-center py-2 px-2">D</th>
                                                    <th className="text-center py-2 px-2">L</th>
                                                    <th className="text-center py-2 px-2">GD</th>
                                                    <th className="text-center py-2 px-2">Pts</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bracket.standings.map((team, idx) => (
                                                    <tr key={idx} className="border-b border-white/5">
                                                        <td className="py-2 px-2 font-bold text-primary">{idx + 1}</td>
                                                        <td className="py-2 px-2">{team.team_name}</td>
                                                        <td className="py-2 px-2 text-center">{team.played}</td>
                                                        <td className="py-2 px-2 text-center text-green-400">{team.won}</td>
                                                        <td className="py-2 px-2 text-center text-yellow-400">{team.drawn}</td>
                                                        <td className="py-2 px-2 text-center text-red-400">{team.lost}</td>
                                                        <td className="py-2 px-2 text-center">{team.goal_difference}</td>
                                                        <td className="py-2 px-2 text-center font-bold">{team.points}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowPreviewDialog(false)} className="border-white/10">
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirmation Dialog for Regenerating Bracket */}
            <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl uppercase flex items-center gap-2 text-red-400">
                            <AlertTriangle className="w-5 h-5" />
                            Warning: Existing Scores Found
                        </DialogTitle>
                        <DialogDescription className="text-muted-foreground">
                            This action will delete all existing match data.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="py-4 space-y-4">
                        <div className="bg-red-500/10 border border-red-500/30 p-4 rounded">
                            <p className="text-red-400 font-semibold">
                                {matchesWithScores} match(es) have scores recorded
                            </p>
                            <p className="text-sm text-muted-foreground mt-2">
                                Regenerating the bracket will permanently delete all existing scores, MVPs, and match results. This action cannot be undone.
                            </p>
                        </div>
                        
                        <div className="text-sm text-muted-foreground">
                            <p className="font-semibold text-white mb-2">Alternatives:</p>
                            <ul className="list-disc list-inside space-y-1">
                                <li>Use the <span className="text-primary">Matches tab</span> to edit individual scores</li>
                                <li>Click "View" on the bracket to edit specific matches</li>
                            </ul>
                        </div>
                    </div>
                    
                    <DialogFooter className="gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => setShowConfirmDialog(false)} 
                            className="border-white/10"
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={() => generateBracket(true)}
                            className="bg-red-600 hover:bg-red-700"
                            disabled={generating}
                        >
                            {generating ? (
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Trash2 className="w-4 h-4 mr-2" />
                            )}
                            Delete & Regenerate
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Match Dialog */}
            <Dialog open={showEditMatchDialog} onOpenChange={setShowEditMatchDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl uppercase flex items-center gap-2">
                            <Edit className="w-5 h-5 text-primary" />
                            Edit Match
                        </DialogTitle>
                    </DialogHeader>
                    
                    {editingMatch && (
                        <div className="py-4 space-y-4">
                            {/* Match Info */}
                            <div className="bg-[#0a1628] p-4 border border-white/10 rounded text-center">
                                <p className="text-lg font-bold">
                                    {editingMatch.home_team} vs {editingMatch.away_team}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {editingMatch.round}
                                </p>
                            </div>
                            
                            {/* Score */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">
                                        {editingMatch.home_team} Score
                                    </Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={editMatchForm.home_score}
                                        onChange={(e) => setEditMatchForm({
                                            ...editMatchForm,
                                            home_score: parseInt(e.target.value) || 0
                                        })}
                                        className="bg-[#0a1628] border-white/10 text-center text-lg"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">
                                        {editingMatch.away_team} Score
                                    </Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={editMatchForm.away_score}
                                        onChange={(e) => setEditMatchForm({
                                            ...editMatchForm,
                                            away_score: parseInt(e.target.value) || 0
                                        })}
                                        className="bg-[#0a1628] border-white/10 text-center text-lg"
                                    />
                                </div>
                            </div>
                            
                            {/* Date and Time */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">Date</Label>
                                    <Input
                                        type="date"
                                        value={editMatchForm.scheduled_date}
                                        onChange={(e) => setEditMatchForm({
                                            ...editMatchForm,
                                            scheduled_date: e.target.value
                                        })}
                                        className="bg-[#0a1628] border-white/10"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">Time</Label>
                                    <Input
                                        type="time"
                                        value={editMatchForm.scheduled_time}
                                        onChange={(e) => setEditMatchForm({
                                            ...editMatchForm,
                                            scheduled_time: e.target.value
                                        })}
                                        className="bg-[#0a1628] border-white/10"
                                    />
                                </div>
                            </div>
                            
                            {/* Venue */}
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Venue</Label>
                                <Input
                                    value={editMatchForm.venue}
                                    onChange={(e) => setEditMatchForm({
                                        ...editMatchForm,
                                        venue: e.target.value
                                    })}
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter className="gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => setShowEditMatchDialog(false)} 
                            className="border-white/10"
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={saveMatchEdit}
                            className="bg-primary hover:bg-cyan-400 text-[#0a1628]"
                        >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Individual Sports Preview Dialog */}
            <Dialog open={showIndividualPreviewDialog} onOpenChange={setShowIndividualPreviewDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-4xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl uppercase flex items-center gap-2">
                            <Timer className="w-5 h-5 text-primary" />
                            {individualEvents?.event_name || Object.keys(individualEvents?.events_by_name || {})[0] || 'Event'} - Heats & Finals
                        </DialogTitle>
                    </DialogHeader>
                    
                    {individualEvents && (
                        <div className="space-y-6 py-4">
                            {/* Summary */}
                            <div className="grid grid-cols-3 gap-4">
                                <div className="bg-[#0a1628] p-4 border border-white/10 text-center">
                                    <p className="text-2xl font-bold text-primary">{individualEvents.total_events}</p>
                                    <p className="text-xs text-muted-foreground">Total Events</p>
                                </div>
                                <div className="bg-[#0a1628] p-4 border border-white/10 text-center">
                                    <p className="text-2xl font-bold text-cyan-400">{individualEvents.total_athletes || individualEvents.all_events?.length || '-'}</p>
                                    <p className="text-xs text-muted-foreground">Athletes</p>
                                </div>
                                <div className="bg-[#0a1628] p-4 border border-white/10 text-center">
                                    <p className="text-2xl font-bold text-green-400">
                                        {individualEvents.rounds?.length || Object.keys(Object.values(individualEvents.events_by_name || {})[0]?.rounds || {}).length || 0}
                                    </p>
                                    <p className="text-xs text-muted-foreground">Rounds</p>
                                </div>
                            </div>
                            
                            {/* Rounds - Handle both formats */}
                            <div className="space-y-4">
                                {/* Direct generate response format (rounds array) */}
                                {individualEvents.rounds?.map((round, rIdx) => (
                                    <div key={rIdx} className="bg-[#0a1628] border border-white/10 p-4">
                                        <h4 className="font-semibold text-primary mb-3 flex items-center gap-2">
                                            {round.name === 'Finals' ? (
                                                <Medal className="w-4 h-4" />
                                            ) : (
                                                <Timer className="w-4 h-4" />
                                            )}
                                            {round.name}
                                            <span className="text-xs text-muted-foreground ml-2">
                                                ({round.events?.length} heats)
                                            </span>
                                        </h4>
                                        {round.qualification_rule && (
                                            <p className="text-xs text-muted-foreground mb-3 bg-[#0d2847] p-2 inline-block">
                                                {round.qualification_rule}
                                            </p>
                                        )}
                                        <div className="space-y-3">
                                            {round.events?.map((event, eIdx) => (
                                                <div 
                                                    key={eIdx}
                                                    className="bg-[#0d2847] border border-white/5 p-3"
                                                >
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="font-semibold text-sm">
                                                            {event.display_name}
                                                        </span>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs text-muted-foreground">
                                                                {event.date} • {event.time}
                                                            </span>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleEditHeat(event)}
                                                                className="h-7 px-2"
                                                            >
                                                                <Edit className="w-3 h-3 mr-1" />
                                                                Results
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    {/* Lanes Table */}
                                                    {event.participants?.length > 0 && (
                                                        <table className="w-full text-xs">
                                                            <thead>
                                                                <tr className="border-b border-white/10">
                                                                    <th className="text-left py-1 px-2 w-12">Lane</th>
                                                                    <th className="text-left py-1 px-2">Athlete</th>
                                                                    <th className="text-center py-1 px-2 w-20">Time</th>
                                                                    <th className="text-center py-1 px-2 w-12">Pos</th>
                                                                    <th className="text-center py-1 px-2 w-12">Q</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {event.participants?.map((p, pIdx) => (
                                                                    <tr key={pIdx} className="border-b border-white/5">
                                                                        <td className="py-1 px-2 font-bold text-primary">{p.lane}</td>
                                                                        <td className="py-1 px-2">{p.athlete_name}</td>
                                                                        <td className="py-1 px-2 text-center font-mono">
                                                                            {p.time || '-'}
                                                                        </td>
                                                                        <td className="py-1 px-2 text-center">
                                                                            {p.position ? (
                                                                                <span className={p.position <= 3 ? 'text-yellow-400 font-bold' : ''}>
                                                                                    {p.position}
                                                                                </span>
                                                                            ) : '-'}
                                                                        </td>
                                                                        <td className="py-1 px-2 text-center">
                                                                            {p.qualified ? (
                                                                                <CheckCircle className="w-3 h-3 text-green-400 inline" />
                                                                            ) : '-'}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                                
                                {/* Loaded from API format (events_by_name) */}
                                {!individualEvents.rounds && individualEvents.events_by_name && 
                                    Object.entries(individualEvents.events_by_name).map(([eventName, eventData]) => (
                                        <div key={eventName} className="space-y-4">
                                            {Object.entries(eventData.rounds || {}).map(([roundName, events]) => (
                                                <div key={roundName} className="bg-[#0a1628] border border-white/10 p-4">
                                                    <h4 className="font-semibold text-primary mb-3 flex items-center gap-2">
                                                        {roundName === 'Finals' || roundName === 'Final' ? (
                                                            <Medal className="w-4 h-4" />
                                                        ) : (
                                                            <Timer className="w-4 h-4" />
                                                        )}
                                                        {roundName}
                                                        <span className="text-xs text-muted-foreground ml-2">
                                                            ({events.length} {roundName === 'Heats' ? 'heats' : 'events'})
                                                        </span>
                                                    </h4>
                                                    <div className="space-y-3">
                                                        {events.map((event, eIdx) => (
                                                            <div 
                                                                key={eIdx}
                                                                className="bg-[#0d2847] border border-white/5 p-3"
                                                            >
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <span className="font-semibold text-sm">
                                                                        {event.display_name}
                                                                    </span>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-xs text-muted-foreground">
                                                                            {event.date} • {event.time}
                                                                        </span>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => handleEditHeat(event)}
                                                                            className="h-7 px-2"
                                                                        >
                                                                            <Edit className="w-3 h-3 mr-1" />
                                                                            Results
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                                {/* Lanes Table */}
                                                                {event.participants?.length > 0 && (
                                                                    <table className="w-full text-xs">
                                                                        <thead>
                                                                            <tr className="border-b border-white/10">
                                                                                <th className="text-left py-1 px-2 w-12">Lane</th>
                                                                                <th className="text-left py-1 px-2">Athlete</th>
                                                                                <th className="text-center py-1 px-2 w-20">Time</th>
                                                                                <th className="text-center py-1 px-2 w-12">Pos</th>
                                                                                <th className="text-center py-1 px-2 w-12">Q</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {event.participants?.map((p, pIdx) => (
                                                                                <tr key={pIdx} className="border-b border-white/5">
                                                                                    <td className="py-1 px-2 font-bold text-primary">{p.lane}</td>
                                                                                    <td className="py-1 px-2">{p.athlete_name}</td>
                                                                                    <td className="py-1 px-2 text-center font-mono">
                                                                                        {p.time || '-'}
                                                                                    </td>
                                                                                    <td className="py-1 px-2 text-center">
                                                                                        {p.position ? (
                                                                                            <span className={p.position <= 3 ? 'text-yellow-400 font-bold' : ''}>
                                                                                                {p.position}
                                                                                            </span>
                                                                                        ) : '-'}
                                                                                    </td>
                                                                                    <td className="py-1 px-2 text-center">
                                                                                        {p.qualified ? (
                                                                                            <CheckCircle className="w-3 h-3 text-green-400 inline" />
                                                                                        ) : '-'}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowIndividualPreviewDialog(false)} className="border-white/10">
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Heat Results Dialog */}
            <Dialog open={showEditHeatDialog} onOpenChange={setShowEditHeatDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl uppercase flex items-center gap-2">
                            <Timer className="w-5 h-5 text-primary" />
                            Enter Results - {editingHeat?.display_name}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {editingHeat && (
                        <div className="py-4 space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Enter finishing times. Positions will be calculated automatically.
                            </p>
                            
                            <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                {heatResults.map((result, idx) => (
                                    <div key={idx} className="flex items-center gap-3 p-2 bg-[#0a1628] border border-white/10">
                                        <div className="w-10 h-10 bg-primary/20 flex items-center justify-center font-bold text-primary">
                                            {result.lane}
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">{result.athlete_name}</p>
                                        </div>
                                        <Input
                                            value={result.time}
                                            onChange={(e) => {
                                                const updated = [...heatResults];
                                                updated[idx].time = e.target.value;
                                                setHeatResults(updated);
                                            }}
                                            placeholder="10.45"
                                            className="w-24 bg-[#0d2847] border-white/10 text-center font-mono"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter className="gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => setShowEditHeatDialog(false)} 
                            className="border-white/10"
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={saveHeatResults}
                            className="bg-primary hover:bg-cyan-400 text-[#0a1628]"
                        >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Save Results
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Add Discipline Dialog */}
            <Dialog open={showAddDisciplineDialog} onOpenChange={setShowAddDisciplineDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
                            <Activity className="w-5 h-5 text-primary" />
                            Add Discipline
                        </DialogTitle>
                        <DialogDescription className="text-muted-foreground">
                            Select a discipline and configure its schedule
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* Discipline Selection */}
                        <div>
                            <Label className="text-xs text-muted-foreground mb-2 block">Discipline</Label>
                            <Select 
                                value={newDiscipline.name}
                                onValueChange={handleDisciplineSelect}
                            >
                                <SelectTrigger className="bg-[#0a1628] border-white/10">
                                    <SelectValue placeholder="Select discipline..." />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0d2847] border-white/10 max-h-[300px]">
                                    {sportType === 'athletics' && (
                                        <>
                                            <SelectItem value="100 Meters">100 Meters</SelectItem>
                                            <SelectItem value="200 Meters">200 Meters</SelectItem>
                                            <SelectItem value="400 Meters">400 Meters</SelectItem>
                                            <SelectItem value="800 Meters">800 Meters</SelectItem>
                                            <SelectItem value="1500 Meters">1500 Meters</SelectItem>
                                            <SelectItem value="3000 Meters">3000 Meters</SelectItem>
                                            <SelectItem value="400 Meters Relay">400m Relay</SelectItem>
                                            <SelectItem value="Long Jump">Long Jump</SelectItem>
                                            <SelectItem value="High Jump">High Jump</SelectItem>
                                            <SelectItem value="Triple Jump">Triple Jump</SelectItem>
                                            <SelectItem value="Shot Put">Shot Put</SelectItem>
                                            <SelectItem value="Discus Throw">Discus Throw</SelectItem>
                                            <SelectItem value="Javelin Throw">Javelin Throw</SelectItem>
                                        </>
                                    )}
                                    {sportType === 'swimming' && (
                                        <>
                                            <SelectItem value="50m Freestyle">50m Freestyle</SelectItem>
                                            <SelectItem value="100m Freestyle">100m Freestyle</SelectItem>
                                            <SelectItem value="200m Freestyle">200m Freestyle</SelectItem>
                                            <SelectItem value="50m Backstroke">50m Backstroke</SelectItem>
                                            <SelectItem value="100m Backstroke">100m Backstroke</SelectItem>
                                            <SelectItem value="50m Breaststroke">50m Breaststroke</SelectItem>
                                            <SelectItem value="100m Breaststroke">100m Breaststroke</SelectItem>
                                            <SelectItem value="50m Butterfly">50m Butterfly</SelectItem>
                                            <SelectItem value="100m Butterfly">100m Butterfly</SelectItem>
                                            <SelectItem value="200m Individual Medley">200m IM</SelectItem>
                                        </>
                                    )}
                                    {sportType === 'cycling' && (
                                        <>
                                            <SelectItem value="Sprint">Sprint</SelectItem>
                                            <SelectItem value="Time Trial">Time Trial</SelectItem>
                                            <SelectItem value="Team Pursuit">Team Pursuit</SelectItem>
                                            <SelectItem value="Individual Pursuit">Individual Pursuit</SelectItem>
                                        </>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Age Category Selection */}
                        <div>
                            <Label className="text-xs text-muted-foreground mb-2 block">Age Category</Label>
                            <Select 
                                value={newDiscipline.ageCategory || 'Open'}
                                onValueChange={(v) => setNewDiscipline({ ...newDiscipline, ageCategory: v })}
                            >
                                <SelectTrigger className="bg-[#0a1628] border-white/10">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0d2847] border-white/10">
                                    {AGE_CATEGORIES.map(cat => (
                                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Number of Venues & Venue Names */}
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs text-muted-foreground mb-2 block">Number of Venues (Parallel)</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        max="20"
                                        value={newDiscipline.numVenues || 1}
                                        onChange={(e) => {
                                            const newNum = Math.min(20, parseInt(e.target.value) || 1);
                                            const currentNames = newDiscipline.venueNames || [];
                                            if (newNum > currentNames.length) {
                                                for (let i = currentNames.length; i < newNum; i++) {
                                                    currentNames.push(`Track ${i + 1}`);
                                                }
                                            }
                                            setNewDiscipline({ 
                                                ...newDiscipline, 
                                                numVenues: newNum,
                                                venueNames: currentNames
                                            });
                                        }}
                                        className="bg-[#0a1628] border-white/10"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground mb-2 block">Primary Venue Type</Label>
                                    <Select 
                                        value={newDiscipline.venue}
                                        onValueChange={(v) => setNewDiscipline({ ...newDiscipline, venue: v })}
                                    >
                                        <SelectTrigger className="bg-[#0a1628] border-white/10">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#0d2847] border-white/10">
                                            {individualVenues.map(v => (
                                                <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            
                            {/* Venue Names Grid when multiple venues */}
                            {(newDiscipline.numVenues || 1) > 1 && (
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                        <MapPin className="w-3 h-3 text-primary" />
                                        {newDiscipline.numVenues} venues - all heats run simultaneously
                                    </Label>
                                    <div className={`grid gap-1 max-h-[120px] overflow-y-auto ${(newDiscipline.numVenues || 1) > 8 ? 'grid-cols-4' : (newDiscipline.numVenues || 1) > 4 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                                        {Array.from({ length: newDiscipline.numVenues || 1 }).map((_, idx) => (
                                            <Input
                                                key={idx}
                                                value={(newDiscipline.venueNames || [])[idx] || `Track ${idx + 1}`}
                                                onChange={(e) => {
                                                    const newNames = [...(newDiscipline.venueNames || [])];
                                                    newNames[idx] = e.target.value;
                                                    setNewDiscipline({ ...newDiscipline, venueNames: newNames });
                                                }}
                                                placeholder={`Track ${idx + 1}`}
                                                className="bg-[#0a1628] border-white/10 text-xs h-7"
                                            />
                                        ))}
                                    </div>
                                    <p className="text-xs text-yellow-400">
                                        {newDiscipline.numVenues} heats at {newDiscipline.startTime} on {newDiscipline.numVenues} different tracks
                                    </p>
                                </div>
                            )}
                        </div>
                        
                        {/* Time Configuration */}
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label className="text-xs text-muted-foreground">Start Time</Label>
                                <Input
                                    type="time"
                                    value={newDiscipline.startTime}
                                    onChange={(e) => setNewDiscipline({ ...newDiscipline, startTime: e.target.value })}
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground">Duration (min)</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    max="120"
                                    value={newDiscipline.duration}
                                    onChange={(e) => setNewDiscipline({ ...newDiscipline, duration: parseInt(e.target.value) || 10 })}
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground">Interval (min)</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    max="30"
                                    value={newDiscipline.interval}
                                    onChange={(e) => setNewDiscipline({ ...newDiscipline, interval: parseInt(e.target.value) || 5 })}
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                        </div>
                        
                        {/* Heat Configuration */}
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label className="text-xs text-muted-foreground">Athletes/Heat</Label>
                                <Input
                                    type="number"
                                    min="4"
                                    max="10"
                                    value={newDiscipline.athletesPerHeat}
                                    onChange={(e) => setNewDiscipline({ ...newDiscipline, athletesPerHeat: parseInt(e.target.value) || 8 })}
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground">Auto Qualify</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    max="4"
                                    value={newDiscipline.qualifiersPerHeat}
                                    onChange={(e) => setNewDiscipline({ ...newDiscipline, qualifiersPerHeat: parseInt(e.target.value) || 2 })}
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground">Time Qualify</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    max="4"
                                    value={newDiscipline.timeQualifiers}
                                    onChange={(e) => setNewDiscipline({ ...newDiscipline, timeQualifiers: parseInt(e.target.value) || 2 })}
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                        </div>
                        
                        {/* Info Box */}
                        <div className="bg-[#0a1628] p-3 border border-white/10 text-xs text-muted-foreground">
                            <p className="font-semibold text-white mb-1">Will use athletes from:</p>
                            <p>{seededAthletes.length} athletes currently loaded</p>
                        </div>
                    </div>
                    
                    <DialogFooter className="gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => setShowAddDisciplineDialog(false)} 
                            className="border-white/10"
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={addDiscipline}
                            disabled={!newDiscipline.name}
                            className="bg-primary hover:bg-cyan-400 text-[#0a1628]"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Discipline
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default BracketTab;
